import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import theme from './DocumentList.module.scss';
import Dropzone from 'react-dropzone';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import DocumentListItemDetails from './DocumentListItemDetails';
import { ResidentialUnitDocument, ChangedItem, BaseDocument, ProjectDocument, DocumentStatus } from '@/types';
import { ExpandableCard, CardSection, ExpandableCardHandle, CardDropdownMenu, CardRow } from '@/components/Cards';
import { DateDisplay } from '@/components/localization';
import { DocumentStatusIndicator } from '@/components/StatusIndicator';
import { DocumentListMode } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import {
    faUpload,
    faEyeSlash,
    faEye,
    faLockOpen,
    faLock,
    faDraftingCompass,
    faArchive,
    faHistory,
} from '@fortawesome/pro-light-svg-icons';
import {
    getOpenDocumentUrl,
    setDocumentHiddenFromBuyer,
    setDocumentIsSensitive,
    setDocumentIsArchived,
} from '@/services/documents';
import { useDocumentType, useMessageDismissal, useIsSeller } from '@/utils/hooks';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import { Tooltip } from '@/components/Tooltip';
import SensitiveInfoTooltip from '@/components/SensitiveInfoTooltip';
import { DismissibleMessageKey } from '@/services/message-dismissal';
import SensitiveInfoConfirmDialog from '@/components/SensitiveInfoConfirmDialog';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { FileType } from '@ygtechnologies/rubus-frontend-utilities';
import ModelViewerModal from '@/components/ModelViewer/ModelViewerModal';
import HistoryLogModal from '@/seller/components/HistoryLog/HistoryLogModal';
import { BadgeColor, BadgeList, BorderBadge, FileIcon, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { useLingui } from '@lingui/react';
import { Checkbox, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { RejectedStatusReason } from '@/components/StatusReason';

function documentStatusToColor(status: DocumentStatus): BadgeColor {
    switch (status) {
        case DocumentStatus.Approved:
            return 'success';
        case DocumentStatus.Rejected:
            return 'danger';
        case DocumentStatus.Pending:
            return 'warning';
        default:
            return 'info';
    }
}

export interface DocumentListItemProps {
    document: BaseDocument;
    onChange?: (changes: ChangedItem<BaseDocument | ResidentialUnitDocument>[]) => void;
    onFileDrop?: (file: File | null, document: BaseDocument | null) => void;
    mode: DocumentListMode;
    defaultExpanded?: boolean;

    isSelected?: boolean;
    onCheckboxChange?: (document: BaseDocument) => void;
}

const DocumentListItem: React.FC<DocumentListItemProps> = ({
    document,
    onChange,
    mode,
    onFileDrop,
    defaultExpanded,
    isSelected,
    onCheckboxChange,
}) => {
    const { _ } = useLingui();
    const [expanded, setExpanded] = useState(!!defaultExpanded);
    const [isLoaded, setIsLoaded] = useState(false);

    const projectId = useSelector((state: IState) => state.global.activeProject?.id ?? state.buyer.projectData!.projectId);
    const [sensitiveDialogDismissed, dismissSensitiveDialog] = useMessageDismissal(
        DismissibleMessageKey.SensitiveInfo,
        projectId
    );
    const [showSensitiveDialog, setShowSensitiveDialog] = useState(false);
    const [modalViewerIsOpen, setModalViewerIsOpen] = useState(false);
    const [logModalIsOpen, setLogModalIsOpen] = useState(false);
    const isSeller = useIsSeller();

    const handleDrop = (files: File[]) => {
        const file = files[0];

        if (file.size === 0) {
            Toast.error(<Trans>Cannot drag and drop files directly from compressed folders (.ZIP)</Trans>);
            return;
        }

        triggerHandler(onFileDrop, file, document as BaseDocument);
    };
    const toggleHideDocument = async () => {
        const doc = document as ResidentialUnitDocument;
        await setDocumentHiddenFromBuyer(doc.lastVersionId, !doc.hideFromBuyer);

        triggerHandler(onChange, [
            ChangedItem.fromMerge<ResidentialUnitDocument>(document as any, { hideFromBuyer: !doc.hideFromBuyer }),
        ]);
    };

    const setSensitive = async (flag: boolean) => {
        await setDocumentIsSensitive(document.lastVersionId, flag);

        triggerHandler(onChange, [ChangedItem.fromMerge<ResidentialUnitDocument>(document as any, { isSensitive: flag })]);
    };

    const setArchived = async (flag: boolean) => {
        await setDocumentIsArchived(document.lastVersionId, flag);

        triggerHandler(onChange, [ChangedItem.fromMerge<ResidentialUnitDocument>(document as any, { isArchived: flag })]);
    };

    const handleToggleSensitive = (flag: boolean) => {
        if (flag && !sensitiveDialogDismissed) {
            setShowSensitiveDialog(true);
        } else {
            setSensitive(!document.isSensitive);
        }
    };

    const handleSensitiveDialogAccept = (doNotRemind: boolean) => {
        if (doNotRemind) {
            dismissSensitiveDialog();
        }
        setSensitive(true);
    };

    const documentType = useDocumentType(document.documentTypeId);

    const renderDropdown = () => {
        const canUpload = document.permissions.canReplace && !!onFileDrop;
        const canToggleVisibility = document.permissions.canReplace;
        const canToggleIsSensitive = document.permissions.canMarkAsSensitive;
        const canToggleIsArchived = document.permissions.canReplace;

        const showDropdown = canUpload || canToggleVisibility;

        return (
            <>
                <CardDropdownMenu hidden={!showDropdown}>
                    {canUpload && (
                        <Dropdown.Item
                            icon={faUpload}
                            onClick={(e) => {
                                triggerHandler(onFileDrop, null, document);
                            }}>
                            <Trans>Upload a new version</Trans>
                        </Dropdown.Item>
                    )}
                    {canToggleVisibility && (
                        <Dropdown.Item
                            icon={document.hideFromBuyer ? faEye : faEyeSlash}
                            onClick={(e) => {
                                toggleHideDocument();
                            }}>
                            {document.hideFromBuyer ? (
                                <Trans>Show document to buyer</Trans>
                            ) : (
                                <Trans>Hide document from buyer</Trans>
                            )}
                        </Dropdown.Item>
                    )}
                    {canToggleIsSensitive && (
                        <Dropdown.Item
                            icon={document.isSensitive ? faLockOpen : faLock}
                            onClick={(e) => {
                                handleToggleSensitive(!document.isSensitive);
                            }}>
                            {document.isSensitive ? <Trans>Unmark as sensitive</Trans> : <Trans>Mark as sensitive</Trans>}
                        </Dropdown.Item>
                    )}

                    {canToggleIsArchived && (
                        <Dropdown.Item
                            icon={faArchive}
                            onClick={(e) => {
                                setArchived(!document.isArchived);
                            }}>
                            {document.isArchived ? <Trans>Unarchive document</Trans> : <Trans>Archive document</Trans>}
                        </Dropdown.Item>
                    )}

                    {document.lastVersionFileMetadata.fileType === FileType.IfcModel && (
                        <Dropdown.Item
                            icon={faDraftingCompass}
                            onClick={(e) => {
                                setModalViewerIsOpen(true);
                            }}>
                            <Trans>View BIM model</Trans>
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item icon={faHistory} onClick={() => setLogModalIsOpen(true)}>
                        <Trans>View log</Trans>
                    </Dropdown.Item>
                </CardDropdownMenu>

                {canToggleIsSensitive && (
                    <SensitiveInfoConfirmDialog
                        profileId={document.ownerProfileId}
                        show={showSensitiveDialog}
                        onClose={() => setShowSensitiveDialog(false)}
                        onAccept={handleSensitiveDialogAccept}
                    />
                )}
            </>
        );
    };

    const renderContent = () => {
        const d = document as BaseDocument;

        return (
            <>
                <ScrollToWrapper shouldScroll={defaultExpanded && isLoaded}>
                    <ExpandableCard
                        expanded={expanded}
                        className={theme.documentCard}
                        type="outside"
                        wrap
                        expandableContent={
                            <DocumentListItemDetails
                                onLoad={() => setIsLoaded(true)}
                                key={document.lastVersionId}
                                document={document}
                                onChange={onChange}
                                mode={mode}
                            />
                        }>
                        <div className={theme.selectableDocumentListItem}>
                            <Checkbox
                                inputSize="md"
                                className={theme.selectableDocumentCheckbox}
                                checked={isSelected}
                                onChange={() => {
                                    onCheckboxChange?.(document);
                                }}
                            />

                            <CardRow>
                                <div className={theme.replaceDocumentOverlay}>
                                    <Trans>Drop here to upload a new version of this document</Trans>
                                </div>

                                <div className={theme.info}>
                                    <a
                                        href={getOpenDocumentUrl(document.lastVersionId)}
                                        target="_blank"
                                        rel="noreferrer noopener">
                                        <div className={theme.fileIconWrapper}>
                                            <FileIcon value={document.lastVersionFileMetadata} size="3x" />
                                        </div>
                                    </a>

                                    <div className={theme.type}>
                                        <a
                                            href={getOpenDocumentUrl(document.lastVersionId)}
                                            className={theme.documentLabel}
                                            target="_blank"
                                            rel="noreferrer noopener">
                                            <div>{documentType && documentType.name}</div>
                                            <small>{document.name}</small>
                                        </a>

                                        <div className={theme.visibility}>
                                            {mode === 'ResidentialUnit' && document.isProjectDocument && (
                                                <small>
                                                    <FontAwesomeIcon icon={faBuilding} />
                                                    <Trans>Project document</Trans>
                                                </small>
                                            )}

                                            {mode === 'Project' && document.isArchived && (
                                                <small>
                                                    <FontAwesomeIcon icon={faArchive} />
                                                    <Trans>Archived</Trans>
                                                </small>
                                            )}
                                            {document.isSensitive && (
                                                <SensitiveInfoTooltip profileId={d.ownerProfileId}>
                                                    <small>
                                                        <span className={theme.smallInfo}>
                                                            <FontAwesomeIcon icon={faLock} />
                                                            <Trans>Sensitive</Trans>
                                                        </span>
                                                    </small>
                                                </SensitiveInfoTooltip>
                                            )}
                                            {document.hideFromBuyer && (
                                                <small>
                                                    <FontAwesomeIcon icon={faEyeSlash} />
                                                    <Trans>Hidden from buyer</Trans>
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <CardSection caption={<Trans>Modified</Trans>} right width="250px" shrink>
                                    <div>
                                        <DateDisplay value={document.lastVersionDate} />
                                        {isSeller && d.uploadedByName && (
                                            <Trans>
                                                <small>&nbsp;by {d.uploadedByName}</small>
                                            </Trans>
                                        )}
                                    </div>
                                </CardSection>

                                {mode === 'ResidentialUnit' && (
                                    <CardSection caption={<Trans>Internal status</Trans>} right width="130px">
                                        <DocumentStatusIndicator
                                            value={(document as ResidentialUnitDocument).status}
                                            isArchived={document.isArchived}
                                            size="small"
                                        />

                                        {(document as ResidentialUnitDocument).status === DocumentStatus.Rejected && (
                                            <RejectedStatusReason
                                                reason={(document as ResidentialUnitDocument).lastVersionRejectReason || ''}
                                                userName={''} //TODO: add username
                                            />
                                        )}
                                    </CardSection>
                                )}

                                <ExpandableCardHandle expanded={expanded} onExpandToggle={() => setExpanded(!expanded)} />
                                {renderDropdown()}
                            </CardRow>
                            <CardRow>
                                {mode === 'Project' && (
                                    <div className={theme.linkedUnits}>
                                        <Trans>Linked residential units</Trans>:
                                        <BadgeList>
                                            {(document as ProjectDocument).linkedUnits.map((link) => (
                                                <Tooltip
                                                    key={link.residentialUnitId}
                                                    content={
                                                        <>
                                                            {_({ id: `DocumentStatus.${link.status}` })}
                                                            {link.status === DocumentStatus.Rejected && (
                                                                <>
                                                                    {': '}
                                                                    {link.rejectReason}
                                                                </>
                                                            )}
                                                        </>
                                                    }>
                                                    <BorderBadge color={documentStatusToColor(link.status)}>
                                                        {link.residentialUnitName}
                                                    </BorderBadge>
                                                </Tooltip>
                                            ))}
                                        </BadgeList>
                                    </div>
                                )}
                            </CardRow>
                        </div>
                    </ExpandableCard>
                </ScrollToWrapper>
                {d.lastVersionFileMetadata.fileType === FileType.IfcModel && (
                    <ModelViewerModal
                        toggle={() => setModalViewerIsOpen(!modalViewerIsOpen)}
                        versionId={d.lastVersionId}
                        isOpen={modalViewerIsOpen}
                    />
                )}
                <HistoryLogModal
                    hiddenColumns={['objectType']}
                    toggle={() => setLogModalIsOpen(false)}
                    isOpen={logModalIsOpen}
                    filter={{
                        projectId: projectId,
                        groupByIds: [document.documentId],
                    }}
                />
            </>
        );
    };

    if ((document as BaseDocument).permissions.canReplace && onFileDrop) {
        return (
            <Dropzone onDrop={handleDrop} noClick>
                {({ getRootProps, isDragActive }) => (
                    <div
                        {...getRootProps()}
                        onClick={() => {}}
                        style={{ outline: 'none' }}
                        className={isDragActive ? theme.fileOver : ''}>
                        {renderContent()}
                    </div>
                )}
            </Dropzone>
        );
    }
    return <>{renderContent()}</>;
};

export default DocumentListItem;
